import { useState, useEffect, useCallback } from 'react';
import { PageHeader } from '../components/common/PageHeader';
import { DataTable } from '../components/common/DataTable';
import { Calendar, Download, Filter, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MonthlyAttendancePage() {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [summary, setSummary] = useState({
    totalEmployees: 0,
    totalWorkingDays: 0,
    avgAttendance: 0,
  });

  const fetchAttendanceData = useCallback(async () => {
    setLoading(true);
    try {
      const [year, month] = selectedMonth.split('-');
      const response = await axios.get(`${API_URL}/api/attendance/monthly?year=${year}&month=${month}`);
      setAttendanceData(response.data.attendance || []);
      setSummary(response.data.summary || { totalEmployees: 0, totalWorkingDays: 0, avgAttendance: 0 });
    } catch (error) {
      toast.error('Failed to load attendance data');
      // Generate sample data for demo
      setAttendanceData([]);
      setSummary({ totalEmployees: 1, totalWorkingDays: 22, avgAttendance: 0 });
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchAttendanceData();
  }, [fetchAttendanceData]);

  const columns = [
    {
      key: 'employee',
      header: 'Employee',
      render: (row) => (
        <div>
          <p className="font-medium text-white">{row.employee_name}</p>
          <p className="text-xs text-slate-500">{row.employee_id}</p>
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      render: (row) => <span className="text-slate-400">{row.department || '-'}</span>,
    },
    {
      key: 'present_days',
      header: 'Present Days',
      render: (row) => <span className="text-green-400 font-medium">{row.present_days || 0}</span>,
    },
    {
      key: 'absent_days',
      header: 'Absent Days',
      render: (row) => <span className="text-red-400 font-medium">{row.absent_days || 0}</span>,
    },
    {
      key: 'leave_days',
      header: 'Leave Days',
      render: (row) => <span className="text-yellow-400 font-medium">{row.leave_days || 0}</span>,
    },
    {
      key: 'total_hours',
      header: 'Total Hours',
      render: (row) => <span className="text-slate-300">{row.total_hours || 0}h</span>,
    },
    {
      key: 'overtime_hours',
      header: 'Overtime',
      render: (row) => <span className="text-blue-400">{row.overtime_hours || 0}h</span>,
    },
    {
      key: 'attendance_percentage',
      header: 'Attendance %',
      render: (row) => {
        const percentage = row.attendance_percentage || 0;
        const color = percentage >= 90 ? 'text-green-400' : percentage >= 75 ? 'text-yellow-400' : 'text-red-400';
        return <span className={`font-medium ${color}`}>{percentage}%</span>;
      },
    },
  ];

  const handleExport = () => {
    toast.success('Exporting attendance report...');
    // In a real implementation, this would generate a CSV/Excel file
  };

  return (
    <div data-testid="monthly-attendance-page">
      <PageHeader
        title="Monthly Attendance"
        description="View and manage employee attendance records"
        action={
          <Button onClick={handleExport} className="bg-green-600 hover:bg-green-700">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        }
      />

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-green-500/50"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Employees</p>
              <p className="text-xl font-bold text-white">{summary.totalEmployees}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Working Days</p>
              <p className="text-xl font-bold text-white">{summary.totalWorkingDays}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Filter className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Avg Attendance</p>
              <p className="text-xl font-bold text-white">{summary.avgAttendance}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Attendance Table */}
      <DataTable
        columns={columns}
        data={attendanceData}
        loading={loading}
        emptyMessage="No attendance data for selected month"
      />
    </div>
  );
}
